
import {
  defineComponent,
  PropType,
  ref,
  computed,
  onBeforeMount,
  watch,
  nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { store } from '@/store';
import { ParserFieldObject, Property } from '@/types';
import InputText from 'primevue/inputtext';
import PDropdown from 'primevue/dropdown';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import { getTemplateIframeDocument } from '@/components/template-builder/utils/helpers';
import { ValidationRuleData } from '@/components/template-builder/getters';

interface RuleOption {
  value: string;
  label: string;
}

interface NativeRule {
  type: string;
  label: string;
}

interface PresetOption {
  value: string;
  label: string;
  pattern: string;
  message: string;
}

const NATIVE_RULES_BY_TYPE: Record<string, string[]> = {
  text: [],
  url: ['url'],
  email: ['email'],
  tel: ['tel'],
  number: ['number'],
};

const TEXT_RULE_TYPES = ['minlength', 'maxlength', 'pattern'];
const NUMBER_RULE_TYPES = ['min', 'max', 'pattern'];

export default defineComponent({
  name: 'ValidationRules',

  components: {
    InputText,
    PDropdown,
    SpmButton,
  },

  props: {
    configs: {
      type: Object,
      required: true,
    },
    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },
    elementPartType: {
      type: String,
      required: false,
      default: '',
    },
  },

  emits: {
    'on-change-properties': Object,
  },

  setup(props, context) {
    const { t } = useI18n();

    const rules = ref<ValidationRuleData[]>([]);

    const readInputType = (): string => {
      const template = getTemplateIframeDocument();
      const widgetEl = template?.querySelector(props.parserValues.selector);
      if (!widgetEl) return 'text';
      const input = widgetEl.querySelector('.spm_widget_input_text__input');
      return input?.getAttribute('type') || 'text';
    };

    const currentInputType = ref(readInputType());

    const checkInputTypeChange = () => {
      // Wait for DOM to be updated by the setter before reading
      nextTick(() => {
        setTimeout(() => {
          const newType = readInputType();
          if (newType !== currentInputType.value) {
            currentInputType.value = newType;

            // Clean incompatible rules
            const compatibleTypes = newType === 'number' ? NUMBER_RULE_TYPES : TEXT_RULE_TYPES;
            const before = rules.value.length;
            rules.value = rules.value.filter((r) => compatibleTypes.includes(r.type));
            if (rules.value.length !== before) {
              emitChange();
            }
          }
        }, 100);
      });
    };

    // Re-read input type when any toggle/property changes in the editor
    const toggleVersion = computed(() => store.getters['liveEditor/getToggleVersion']);
    watch(toggleVersion, checkInputTypeChange);

    // Also re-read when parserValues change
    watch(() => props.parserValues, checkInputTypeChange, { deep: true });

    const nativeRuleLabels: Record<string, string> = {
      url: t('templateBuilder.formFields.validationNativeTypes.url'),
      email: t('templateBuilder.formFields.validationNativeTypes.email'),
      tel: t('templateBuilder.formFields.validationNativeTypes.tel'),
      number: t('templateBuilder.formFields.validationNativeTypes.number'),
    };

    const nativeRules = computed((): NativeRule[] => {
      const nativeTypes = NATIVE_RULES_BY_TYPE[currentInputType.value] || [];
      return nativeTypes.map((type) => ({
        type,
        label: nativeRuleLabels[type] || type,
      }));
    });

    const allRuleTypes: RuleOption[] = [
      { value: 'minlength', label: t('templateBuilder.formFields.validationRuleTypes.minlength') },
      { value: 'maxlength', label: t('templateBuilder.formFields.validationRuleTypes.maxlength') },
      { value: 'pattern', label: t('templateBuilder.formFields.validationRuleTypes.pattern') },
      { value: 'min', label: t('templateBuilder.formFields.validationRuleTypes.min') },
      { value: 'max', label: t('templateBuilder.formFields.validationRuleTypes.max') },
    ];

    const availableCustomRuleTypes = computed(() => {
      if (currentInputType.value === 'number') {
        return allRuleTypes.filter((r) => NUMBER_RULE_TYPES.includes(r.value));
      }
      return allRuleTypes.filter((r) => TEXT_RULE_TYPES.includes(r.value));
    });

    const presetsByInputType: Record<string, PresetOption[]> = {
      text: [
        // Identité
        { value: 'lettersOnly', label: t('templateBuilder.formFields.validationPresets.lettersOnly'), pattern: '^[A-Za-zÀ-ÿ\\s\\-\\.]+$', message: t('templateBuilder.formFields.validationPresetMessages.lettersOnly') },
        { value: 'fullName', label: t('templateBuilder.formFields.validationPresets.fullName'), pattern: "^[A-Za-zÀ-ÿ\\s\\-\\']+$", message: t('templateBuilder.formFields.validationPresetMessages.fullName') },
        { value: 'alphanumeric', label: t('templateBuilder.formFields.validationPresets.alphanumeric'), pattern: '^[A-Za-z0-9]+$', message: t('templateBuilder.formFields.validationPresetMessages.alphanumeric') },
        { value: 'noSpecialChars', label: t('templateBuilder.formFields.validationPresets.noSpecialChars'), pattern: '^[A-Za-z0-9À-ÿ\\s\\-\\.]+$', message: t('templateBuilder.formFields.validationPresetMessages.noSpecialChars') },
        { value: 'digitsOnly', label: t('templateBuilder.formFields.validationPresets.digitsOnly'), pattern: '^\\d+$', message: t('templateBuilder.formFields.validationPresetMessages.digitsOnly') },
        // Adresses
        { value: 'postalCodeFR', label: t('templateBuilder.formFields.validationPresets.postalCodeFR'), pattern: '^\\d{5}$', message: t('templateBuilder.formFields.validationPresetMessages.postalCodeFR') },
        { value: 'postalCodeGeneric', label: t('templateBuilder.formFields.validationPresets.postalCodeGeneric'), pattern: '^[A-Za-z0-9\\s\\-]{3,10}$', message: t('templateBuilder.formFields.validationPresetMessages.postalCodeGeneric') },
        { value: 'city', label: t('templateBuilder.formFields.validationPresets.city'), pattern: '^[A-Za-zÀ-ÿ\\s\\-]+$', message: t('templateBuilder.formFields.validationPresetMessages.city') },
        // B2B
        { value: 'siret', label: t('templateBuilder.formFields.validationPresets.siret'), pattern: '^\\d{14}$', message: t('templateBuilder.formFields.validationPresetMessages.siret') },
        { value: 'siren', label: t('templateBuilder.formFields.validationPresets.siren'), pattern: '^\\d{9}$', message: t('templateBuilder.formFields.validationPresetMessages.siren') },
        { value: 'vatEU', label: t('templateBuilder.formFields.validationPresets.vatEU'), pattern: '^[A-Z]{2}[A-Z0-9]{2,13}$', message: t('templateBuilder.formFields.validationPresetMessages.vatEU') },
        { value: 'iban', label: t('templateBuilder.formFields.validationPresets.iban'), pattern: '^[A-Z]{2}\\d{2}[A-Z0-9]{4}\\d{7}([A-Z0-9]?){0,16}$', message: t('templateBuilder.formFields.validationPresetMessages.iban') },
        // E-commerce
        { value: 'orderNumber', label: t('templateBuilder.formFields.validationPresets.orderNumber'), pattern: '^[A-Za-z0-9\\-]{3,30}$', message: t('templateBuilder.formFields.validationPresetMessages.orderNumber') },
        { value: 'couponCode', label: t('templateBuilder.formFields.validationPresets.couponCode'), pattern: '^[A-Z0-9\\-]{3,20}$', message: t('templateBuilder.formFields.validationPresetMessages.couponCode') },
        { value: 'referralCode', label: t('templateBuilder.formFields.validationPresets.referralCode'), pattern: '^[A-Za-z0-9\\-]{3,20}$', message: t('templateBuilder.formFields.validationPresetMessages.referralCode') },
        { value: 'loyaltyCard', label: t('templateBuilder.formFields.validationPresets.loyaltyCard'), pattern: '^[A-Za-z0-9]{6,20}$', message: t('templateBuilder.formFields.validationPresetMessages.loyaltyCard') },
        { value: 'productRef', label: t('templateBuilder.formFields.validationPresets.productRef'), pattern: '^[A-Za-z0-9\\-\\_]{2,30}$', message: t('templateBuilder.formFields.validationPresetMessages.productRef') },
      ],
      tel: [
        { value: 'phoneFR', label: t('templateBuilder.formFields.validationPresets.phoneFR'), pattern: '^(?:(?:\\+33|0)[1-9])\\d{8}$', message: t('templateBuilder.formFields.validationPresetMessages.phoneFR') },
        { value: 'mobileFR', label: t('templateBuilder.formFields.validationPresets.mobileFR'), pattern: '^(?:(?:\\+33|0)[67])\\d{8}$', message: t('templateBuilder.formFields.validationPresetMessages.mobileFR') },
        { value: 'phoneInternational', label: t('templateBuilder.formFields.validationPresets.phoneInternational'), pattern: '^\\+\\d{7,15}$', message: t('templateBuilder.formFields.validationPresetMessages.phoneInternational') },
        { value: 'phoneBE', label: t('templateBuilder.formFields.validationPresets.phoneBE'), pattern: '^(?:\\+32|0)[1-9]\\d{7,8}$', message: t('templateBuilder.formFields.validationPresetMessages.phoneBE') },
        { value: 'phoneCH', label: t('templateBuilder.formFields.validationPresets.phoneCH'), pattern: '^(?:\\+41|0)[1-9]\\d{8}$', message: t('templateBuilder.formFields.validationPresetMessages.phoneCH') },
        { value: 'phoneES', label: t('templateBuilder.formFields.validationPresets.phoneES'), pattern: '^(?:\\+34|0)[6-9]\\d{8}$', message: t('templateBuilder.formFields.validationPresetMessages.phoneES') },
        { value: 'phoneDE', label: t('templateBuilder.formFields.validationPresets.phoneDE'), pattern: '^(?:\\+49|0)[1-9]\\d{6,12}$', message: t('templateBuilder.formFields.validationPresetMessages.phoneDE') },
        { value: 'phoneIT', label: t('templateBuilder.formFields.validationPresets.phoneIT'), pattern: '^(?:\\+39|0)[0-9]\\d{8,10}$', message: t('templateBuilder.formFields.validationPresetMessages.phoneIT') },
        { value: 'phoneUK', label: t('templateBuilder.formFields.validationPresets.phoneUK'), pattern: '^(?:\\+44|0)[1-9]\\d{9}$', message: t('templateBuilder.formFields.validationPresetMessages.phoneUK') },
        { value: 'phoneUS', label: t('templateBuilder.formFields.validationPresets.phoneUS'), pattern: '^(?:\\+1)?[2-9]\\d{9}$', message: t('templateBuilder.formFields.validationPresetMessages.phoneUS') },
      ],
      url: [
        { value: 'httpsOnly', label: t('templateBuilder.formFields.validationPresets.httpsOnly'), pattern: '^https://.*', message: t('templateBuilder.formFields.validationPresetMessages.httpsOnly') },
        { value: 'instagram', label: t('templateBuilder.formFields.validationPresets.instagram'), pattern: '^https?://(www\\.)?instagram\\.com/.+', message: t('templateBuilder.formFields.validationPresetMessages.instagram') },
        { value: 'facebook', label: t('templateBuilder.formFields.validationPresets.facebook'), pattern: '^https?://(www\\.)?facebook\\.com/.+', message: t('templateBuilder.formFields.validationPresetMessages.facebook') },
        { value: 'linkedin', label: t('templateBuilder.formFields.validationPresets.linkedin'), pattern: '^https?://(www\\.)?linkedin\\.com/(in|company)/.+', message: t('templateBuilder.formFields.validationPresetMessages.linkedin') },
        { value: 'tiktok', label: t('templateBuilder.formFields.validationPresets.tiktok'), pattern: '^https?://(www\\.)?tiktok\\.com/@.+', message: t('templateBuilder.formFields.validationPresetMessages.tiktok') },
        { value: 'youtube', label: t('templateBuilder.formFields.validationPresets.youtube'), pattern: '^https?://(www\\.)?(youtube\\.com|youtu\\.be)/.+', message: t('templateBuilder.formFields.validationPresetMessages.youtube') },
        { value: 'pinterest', label: t('templateBuilder.formFields.validationPresets.pinterest'), pattern: '^https?://(www\\.)?pinterest\\.(com|fr)/.+', message: t('templateBuilder.formFields.validationPresetMessages.pinterest') },
        { value: 'twitter', label: t('templateBuilder.formFields.validationPresets.twitter'), pattern: '^https?://(www\\.)?(twitter\\.com|x\\.com)/.+', message: t('templateBuilder.formFields.validationPresetMessages.twitter') },
      ],
      email: [],
      number: [],
    };

    const presets = computed(() => presetsByInputType[currentInputType.value] || []);

    const hasParam = (type: string) => ['minlength', 'maxlength', 'pattern', 'min', 'max'].includes(type);
    const isNumericParam = (type: string) => ['minlength', 'maxlength', 'min', 'max'].includes(type);

    const getRuleLabel = (type: string): string => {
      const found = allRuleTypes.find((r) => r.value === type);
      return found ? found.label : type;
    };

    const getDefaultMessage = (type: string): string => {
      const key = `templateBuilder.formFields.validationDefaultMessages.${type}`;
      // Pass literal {value} so vue-i18n outputs the placeholder verbatim; forms.js substitutes at runtime.
      const translated = t(key, { value: '{value}' });
      return translated !== key ? translated : '';
    };

    const hasValueVariable = (type: string) => ['minlength', 'maxlength', 'min', 'max'].includes(type);

    const emitChange = () => {
      const property: Property = {
        ...props.parserValues.properties[0],
        value: rules.value.map((r) => ({ ...r })),
      };

      context.emit('on-change-properties', {
        selector: props.parserValues.selector,
        properties: [property],
      });
    };

    const addRule = () => {
      const defaultType = availableCustomRuleTypes.value[0]?.value || 'minlength';
      rules.value.push({
        type: defaultType,
        value: '',
        message: getDefaultMessage(defaultType),
        translationKey: '',
      });
      emitChange();
    };

    const removeRule = (idx: number) => {
      rules.value.splice(idx, 1);
      emitChange();
    };

    const onRuleTypeChange = (idx: number) => {
      rules.value[idx].value = '';
      rules.value[idx].message = getDefaultMessage(rules.value[idx].type);
      rules.value[idx].translationKey = '';
      emitChange();
    };

    const applyPreset = (idx: number, presetValue: string) => {
      const preset = presets.value.find((p) => p.value === presetValue);
      if (preset) {
        rules.value[idx].value = preset.pattern;
        rules.value[idx].message = preset.message;
        rules.value[idx].translationKey = '';
        emitChange();
      }
    };

    onBeforeMount(() => {
      const parserRules = props.parserValues.properties[0]?.value;
      if (Array.isArray(parserRules)) {
        rules.value = parserRules.map((r: ValidationRuleData) => ({ ...r }));
      }
    });

    return {
      rules,
      nativeRules,
      availableCustomRuleTypes,
      presets,
      hasParam,
      isNumericParam,
      hasValueVariable,
      getRuleLabel,
      getDefaultMessage,
      emitChange,
      addRule,
      removeRule,
      onRuleTypeChange,
      applyPreset,
      t,
    };
  },
});
